import { getDefaultStaffImage } from "@/utils/avatar.utils";
import { getSpecialtiesRequest } from "@/store/reducers/specialtiesSlice";
import { selectSpecialties } from "@/store/selectors/specialtiesSelector";
import { FacilityStats } from "@/types/facility.definition";
import { LicenceResponse } from "@/types/licence.definition";
import { RoleEnum } from "@/types/user.definition";
import {
  Button,
  DropdownMultiSelect,
  Edit,
  Filter,
  GraduationCap,
  Input,
  LucideProps,
  Mail,
  Phone,
  RefreshCw,
  Search,
  Select,
  Star,
  User,
  UserCheck,
  UserPlus,
  Users,
  X,
  formatPhoneNumberForDisplay,
} from "@ews-admin/global-design-system";
import React, { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Staff, isDoctor } from "../../../types/staff.definition";
import { InlineLoader } from "../../atoms/InlineLoader";
import { Pagination } from "../Pagination/Pagination";
import { SecretaryCard } from "../SecretaryCard/SecretaryCard";

export interface StaffListProps {
  facilityStaffStats: FacilityStats | null;
  licence?: LicenceResponse | null;
  isLoading: boolean;
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  debouncedSearchTerm: string;
  filterSpecialty: string[];
  //TODO v1 filterService
  //filterService: string[];
  //setFilterService: (services: string[]) => void;
  filterRole: "" | RoleEnum.DOCTOR | RoleEnum.SECRETARY;
  setFilterRole: (role: "" | RoleEnum.DOCTOR | RoleEnum.SECRETARY) => void;
  setFilterSpecialty: (specialties: string[]) => void;
  showFilters: boolean;
  setShowFilters: (show: boolean) => void;
  viewMode: "grid" | "list";
  setViewMode: (mode: "grid" | "list") => void;
  currentPage: number;
  setCurrentPage: (page: number) => void;
  itemsPerPage: number;
  setItemsPerPage: (size: number) => void;
  totalStaffCount: number;
  filteredStaff: Staff[];
  onEditStaff: (staff: Staff) => void;
  onViewDetails: (staff: Staff) => void;
  onAddStaff?: () => void;
}

export const StaffList: React.FC<StaffListProps> = ({
  facilityStaffStats,
  licence,
  isLoading,
  searchTerm,
  setSearchTerm,
  debouncedSearchTerm,
  //TODO v1 filterService
  //filterService,
  //setFilterService,
  filterRole,
  setFilterRole,
  filterSpecialty,
  setFilterSpecialty,
  showFilters,
  setShowFilters,
  viewMode,
  setViewMode,
  currentPage,
  setCurrentPage,
  itemsPerPage,
  setItemsPerPage,
  totalStaffCount,
  filteredStaff,
  onEditStaff,
  onViewDetails,
  onAddStaff,
}) => {
  const { t } = useTranslation();

  // A search or a filter is narrowing the list (MED-1653).
  const hasCriteria =
    debouncedSearchTerm.trim() !== "" || filterRole !== "" || filterSpecialty.length > 0;
  const clearCriteria = () => {
    setSearchTerm("");
    setFilterRole("");
    setFilterSpecialty([]);
  };
  const specialties = useSelector(selectSpecialties);
  const dispatch = useDispatch();

  // Create options array directly from all specialties fetched from backend (BFF)
  // Use labels as values for display
  const specialtyOptions = useMemo(
    () =>
      specialties.map((specialty) => ({
        value: specialty.label, // Use label as value for display
        label: specialty.label, // Display label
      })),
    [specialties],
  );

  // Helper function to convert specialty label to code
  const getCodeByLabel = (label: string): string | undefined => {
    const specialty = specialties.find((s) => s.label === label);
    return specialty?.code;
  };

  // Helper function to convert specialty code to label
  const getLabelByCode = (code: string): string => {
    const specialty = specialties.find((s) => s.code === code);
    return specialty?.label || code;
  };

  useEffect(() => {
    if (specialties.length === 0) {
      dispatch(getSpecialtiesRequest());
    }
  }, [dispatch, specialties.length]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    const staffListElement = document.querySelector(".staff-list-container");
    if (staffListElement) {
      staffListElement.scrollIntoView({ behavior: "smooth" });
    }
  };

  const handlePageSizeChange = (newSize: number) => {
    setItemsPerPage(newSize);
    setCurrentPage(1);
  };

  const getRoleIcon = (role: string) => {
    const roleIcons: Record<string, React.ComponentType<LucideProps>> = {
      Doctor: GraduationCap as React.ComponentType<LucideProps>,
      Nurse: User as React.ComponentType<LucideProps>,
      Receptionist: User as React.ComponentType<LucideProps>,
    };
    const Icon = roleIcons[role] || User;
    return (<Icon className="w-4 h-4" />) as React.ReactNode;
  };

  const renderRoleInfo = (roleName: string) => {
    switch (roleName) {
      case RoleEnum.DOCTOR:
        return (
          <>
            {getRoleIcon("Doctor")}
            <span className="ml-2 text-sm text-neutral-900">
              {t("staff.manage.list.doctor")}
            </span>
          </>
        );

      case "SECRETARY":
        return (
          <>
            {getRoleIcon("Secretary")}
            <span className="ml-2 text-sm text-neutral-900">
              {t("staff.manage.list.secretary")}
            </span>
          </>
        );

      default:
        return (
          <>
            {getRoleIcon("User")}
            <span className="ml-2 text-sm text-neutral-900">
              {t("staff.manage.list.user")}
            </span>
          </>
        );
    }
  };

  return (
    <div className="space-y-6">
      {/* Statistics Cards */}
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
        <div className="p-6 card-base">
          <div className="flex items-center">
            <div className="p-2 rounded-lg bg-ews-primary">
              <Users className="w-6 h-6 text-white" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-neutral-600">
                {t("staff.manage.list.stats.totalStaff")}
              </p>
              <p className="text-2xl font-bold text-neutral-900">
                {facilityStaffStats?.summary?.metrics?.totals?.total}
              </p>
            </div>
          </div>
        </div>

        <div className="p-6 card-base">
          <div className="flex items-center">
            <div className="p-2 rounded-lg bg-success-100">
              <UserCheck className="w-6 h-6 text-success-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-neutral-600">
                {t("staff.manage.list.stats.active")}
              </p>
              <p className="text-2xl font-bold text-neutral-900">
                {facilityStaffStats?.summary?.metrics?.totals?.active}
              </p>
            </div>
          </div>
        </div>

        <div className="p-6 card-base">
          <div className="flex items-center">
            <div className="p-2 rounded-lg bg-ews-primary">
              <GraduationCap className="w-6 h-6 text-white" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-neutral-600">
                {t("staff.manage.list.stats.doctors")}
              </p>
              <p className="text-2xl font-bold text-neutral-900">
                {(licence
                  ? facilityStaffStats?.summary?.metrics?.doctors?.active
                  : facilityStaffStats?.summary?.metrics?.doctors?.total) ?? 0}
                {licence && (
                  <span className="ml-1 text-sm font-normal text-neutral-400">
                    / {licence.numberOfDoctors}
                  </span>
                )}
              </p>
              {licence && (
                <p className="mt-0.5 text-xs text-neutral-500">
                  {t("staff.manage.list.stats.slots")}
                </p>
              )}
            </div>
          </div>
        </div>

        <div className="p-6 card-base">
          <div className="flex items-center">
            <div className="p-2 rounded-lg bg-ews-secondary">
              <Users className="w-6 h-6 text-white" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-neutral-600">
                {t("staff.manage.list.stats.secretaries")}
              </p>
              <p className="text-2xl font-bold text-neutral-900">
                {(licence
                  ? facilityStaffStats?.summary?.metrics?.secretaries?.active
                  : facilityStaffStats?.summary?.metrics?.secretaries?.total) ?? 0}
                {licence && (
                  <span className="ml-1 text-sm font-normal text-neutral-400">
                    / {licence.maxSecretaries}
                  </span>
                )}
              </p>
              {licence && (
                <p className="mt-0.5 text-xs text-neutral-500">
                  {t("staff.manage.list.stats.slots")}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="p-6 card-base">
        <div className="flex flex-col lg:flex-row lg:items-center lg:space-x-4">
          <div className="flex-1">
            <div className="relative">
              <Input
                leftIcon={<Search />}
                placeholder={t("staff.manage.list.search.placeholder")}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {searchTerm.trim().length > 0 && (
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                  <div className="w-2 h-2 rounded-full animate-pulse bg-primary-500"></div>
                </div>
              )}
            </div>
            {searchTerm !== debouncedSearchTerm &&
              searchTerm.trim().length > 0 && (
                <p className="mt-1 text-xs text-neutral-500">
                  {t("staff.manage.list.search.searching")}
                </p>
              )}
          </div>
          <div className="flex mt-4 space-x-3 lg:mt-0">
            <Button
              variant={showFilters ? "ews-primary" : "ghost"}
              onClick={() => setShowFilters(!showFilters)}
            >
              <Filter className="mr-2 w-4 h-4" />
              {t("staff.manage.list.filters")}
              {(filterRole || filterSpecialty.length > 0) && (
                <span className="ml-1 px-1.5 py-0.5 text-xs bg-ews-primary-100 text-ews-primary-800 rounded-full">
                  {[filterRole, ...filterSpecialty].filter(Boolean).length}
                </span>
              )}
            </Button>
            <Button
              variant="ghost"
              onClick={() => setViewMode(viewMode === "grid" ? "list" : "grid")}
            >
              {viewMode === "grid"
                ? t("staff.manage.list.listView")
                : t("staff.manage.list.gridView")}
            </Button>
          </div>
        </div>

        {showFilters && (
          <div className="flex items-end gap-4 mt-6">
            <div className="flex-1 grid grid-cols-1 gap-4 md:grid-cols-3">
              <Select
                label={t("staff.manage.list.role")}
                value={filterRole}
                onChange={(value) => {
                  setFilterRole(value as "" | RoleEnum.DOCTOR | RoleEnum.SECRETARY);
                  // Secretaries have no specialty.
                  if (value === RoleEnum.SECRETARY) setFilterSpecialty([]);
                }}
                options={[
                  { value: "", label: t("staff.manage.list.allRoles") },
                  {
                    value: RoleEnum.DOCTOR,
                    label: t("staff.manage.list.doctors"),
                  },
                  {
                    value: "SECRETARY",
                    label: t("staff.manage.list.secretaries"),
                  },
                ]}
              />

              {/* MED-1653: available without picking "Doctor" first; a specialty means doctors only. */}
              {filterRole !== RoleEnum.SECRETARY && (
                <DropdownMultiSelect
                  value={
                    Array.isArray(filterSpecialty)
                      ? filterSpecialty
                        .map((code) => getLabelByCode(code))
                        .filter((label) =>
                          specialtyOptions.some((opt) => opt.value === label),
                        )
                      : []
                  }
                  onChange={(selectedLabels) => {
                    const codes = selectedLabels
                      .map((label) => getCodeByLabel(label) || label)
                      .filter((code): code is string => code !== undefined);
                    setFilterSpecialty(codes);
                  }}
                  searchPlaceholder={t("staff.manage.list.allSpecialties")}
                  placeholder={t("staff.manage.list.allSpecialties")}
                  label={t("staff.manage.list.specialty")}
                  options={specialtyOptions}
                  name="specialties"
                  aria-label="specialties"
                />
              )}
            </div>
            {/* MED-1653: always offered in the panel, disabled while nothing is filtered. */}
            <Button
              variant="ghost"
              disabled={!hasCriteria}
              onClick={clearCriteria}
            >
              <X className="mr-2 w-4 h-4" />
              {t("staff.manage.list.clearFilters")}
            </Button>
          </div>
        )}
      </div>

      {/* Loading and Error States */}
      {isLoading && (
        <InlineLoader message={t("staff.manage.list.loadingStaff")} />
      )}

      {/* MED-1653: a search or filter without result is not "no staff at all". */}
      {!isLoading && totalStaffCount === 0 && filteredStaff.length === 0 && hasCriteria && (
        <div
          className="p-10 text-center card-base"
          data-testid="staff-no-results"
        >
          <p className="mb-6 text-sm text-neutral-600">
            {t("staff.manage.search.noResults")}
          </p>
          <div className="flex justify-center">
            <Button variant="ghost" onClick={clearCriteria}>
              <X className="mr-2 w-4 h-4" />
              {t("staff.manage.list.clearFilters")}
            </Button>
          </div>
        </div>
      )}

      {/* Empty state — no staff at all */}
      {!isLoading && totalStaffCount === 0 && filteredStaff.length === 0 && !hasCriteria && (
        <div
          className="p-10 text-center card-base"
          data-testid="staff-empty-state"
        >
          <div className="flex justify-center items-center mx-auto mb-4 w-16 h-16 rounded-full bg-ews-primary-100">
            <Users className="w-8 h-8 text-primary-600" />
          </div>
          <p className="mb-2 text-lg font-semibold text-neutral-900">
            {t("staff.manage.list.empty.title")}
          </p>
          <p className="mb-6 text-sm text-neutral-600">
            {t("staff.manage.list.empty.message")}
          </p>
          {onAddStaff && (
            <div className="flex justify-center">
              <Button onClick={onAddStaff}>
                <UserPlus className="mr-2 w-4 h-4" />
                {t("staff.manage.list.empty.cta")}
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Staff Grid/List */}
      {!isLoading && totalStaffCount > 0 && (
        <div className="staff-list-container">
          {viewMode === "grid" ? (
            <div className="max-h-[600px] overflow-y-auto">
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {filteredStaff.map((member) => {
                  // If this is a secretary, use the SecretaryCard component
                  if (!isDoctor(member)) {
                    return (
                      <SecretaryCard
                        key={"card" + member.userId}
                        member={member}
                        onEditStaff={onEditStaff}
                        onViewDetails={onViewDetails}
                      />
                    );
                  }
                  if (isDoctor(member)) {
                    // For doctors, use the existing card layout
                    // Use contactInfo if available, otherwise fall back to direct properties
                    const email =
                      member.contactInfo?.email || member.email || "";
                    const phoneNumber = formatPhoneNumberForDisplay(
                      member.contactInfo?.phoneNumber ||
                      member.phoneNumber ||
                      ""
                    );

                    return (
                      <div
                        key={"card" + member.userId}
                        className="overflow-hidden relative bg-white rounded-xl border transition-all duration-300 cursor-pointer border-neutral-200 hover:shadow-xl hover:shadow-neutral-200/50 hover:border-ews-primary hover:-translate-y-1 group"
                        onClick={() => onViewDetails(member)}
                      >
                        {/* Gradient Header Background */}
                        <div className="absolute top-0 right-0 left-0 h-20 bg-ews-primary"></div>

                        {/* Action Buttons - Show on Hover */}
                        <div className="flex absolute top-3 right-3 z-10 space-x-2 opacity-0 transition-opacity duration-200 group-hover:opacity-100">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              onEditStaff(member);
                            }}
                            className="p-2 bg-white rounded-lg shadow-md transition-all hover:bg-neutral-50 hover:scale-110"
                            title={t("staff.manage.list.edit")}
                          >
                            <Edit className="w-4 h-4 text-primary-600" />
                          </button>
                        </div>

                        {/* Profile Section */}
                        <div className="relative px-4 pt-6 pb-4">
                          {/* Password Change Required Badge */}
                          {member.requiresPasswordChange && (
                            <div className="absolute top-2 left-2 z-10">
                              <span className="inline-flex items-center px-2 py-1 text-xs font-medium text-yellow-800 bg-yellow-100 rounded-full border border-yellow-300">
                                <RefreshCw className="mr-1 w-3 h-3" />
                                {t(
                                  "staff.manage.list.passwordChangeRequired",
                                  "Password Required",
                                )}
                              </span>
                            </div>
                          )}
                          {/* Profile Photo */}
                          <div className="relative mx-auto mb-3 w-16 h-16">
                            <div className="absolute inset-0 rounded-full bg-ews-primary p-0.5">
                              <img
                                src={
                                  member.photoUrl ||
                                  getDefaultStaffImage(member.roleName, member.civility)
                                }
                                alt={`${member.firstName} ${member.lastName}`}
                                className="object-cover w-full h-full rounded-full border-4 border-white"
                              />
                            </div>
                          </div>

                          {/* Name and Title */}
                          <div className="mb-3 text-center">
                            <h3 className="mb-1 text-lg font-bold text-neutral-900">
                              {member.roleName === RoleEnum.DOCTOR
                                ? "Dr. "
                                : ""}
                              {member.firstName} {member.lastName}
                            </h3>

                            {/* Role Badge */}
                            <div className="flex justify-center mb-2">
                              <span className="px-2.5 py-1 text-xs font-semibold rounded-full bg-ews-primary text-white">
                                {t("staff.manage.list.doctor")}
                              </span>
                            </div>
                          </div>

                          {/* Divider */}
                          <div className="mb-4 h-px bg-neutral-200"></div>

                          {/* Contact Information */}
                          <div className="space-y-2.5">
                            {email && (
                              <div className="flex items-center text-sm text-neutral-600 group/item">
                                <div className="flex justify-center items-center mr-2 w-8 h-8 rounded-lg transition-colors bg-neutral-100 group-hover/item:bg-neutral-50">
                                  <Mail className="w-3.5 h-3.5 text-neutral-500 group-hover/item:text-ews-primary" />
                                </div>
                                <span className="text-xs truncate">
                                  {email}
                                </span>
                              </div>
                            )}
                            {phoneNumber && (
                              <div className="flex items-center text-sm text-neutral-600 group/item">
                                <div className="flex justify-center items-center mr-2 w-8 h-8 rounded-lg transition-colors bg-neutral-100 group-hover/item:bg-neutral-50">
                                  <Phone className="w-3.5 h-3.5 text-neutral-500 group-hover/item:text-ews-primary" />
                                </div>
                                <span className="text-xs">{phoneNumber}</span>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Footer with specialty info */}
                        <div className="px-4 py-3 border-t bg-neutral-50 border-neutral-100">
                          <div className="flex justify-between items-center text-xs text-neutral-600">
                            {member.specialties &&
                              member.specialties.length > 0 ? (
                              <>
                                <span className="font-medium truncate text-ews-primary">
                                  {member.specialties[0].label}
                                </span>
                                {member.specialties.length > 1 && (
                                  <span className="text-neutral-500">
                                    {t("staff.manage.list.moreSpecialties", {
                                      count: member.specialties.length - 1,
                                    })}
                                  </span>
                                )}
                              </>
                            ) : (
                              <span className="text-neutral-500">
                                {t("staff.manage.list.noSpecialty")}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  }
                })}
              </div>
            </div>
          ) : (
            <div className="max-h-[600px] overflow-y-auto">
              <div className="overflow-hidden card-base">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-neutral-200">
                    <thead className="sticky top-0 z-10 bg-neutral-50">
                      <tr>
                        <th className="px-6 py-3 text-xs font-medium tracking-wider text-left uppercase text-neutral-500">
                          {t("staff.manage.list.table.staffMember")}
                        </th>
                        <th className="px-6 py-3 text-xs font-medium tracking-wider text-left uppercase text-neutral-500">
                          {t("staff.manage.list.table.role")}
                        </th>
                        <th className="px-6 py-3 text-xs font-medium tracking-wider text-left uppercase text-neutral-500">
                          {t("staff.manage.list.table.contact")}
                        </th>
                        {filterRole === RoleEnum.DOCTOR && (
                          <>
                            <th className="px-6 py-3 text-xs font-medium tracking-wider text-left uppercase text-neutral-500">
                              {t("staff.manage.list.table.rating")}
                            </th>
                            <th className="px-6 py-3 text-xs font-medium tracking-wider text-left uppercase text-neutral-500">
                              {t("staff.manage.list.table.availability")}
                            </th>
                          </>
                        )}
                        <th className="px-6 py-3 text-xs font-medium tracking-wider text-left uppercase text-neutral-500">
                          {t("staff.manage.list.table.actions")}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-neutral-200">
                      {filteredStaff.map((member) => (
                          <tr
                            key={member.userId}
                            className="hover:bg-neutral-50"
                          >
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center">
                                <img
                                  src={
                                    member.photoUrl ||
                                    getDefaultStaffImage(member.roleName, member.civility)
                                  }
                                  alt={`${member.firstName} ${member.lastName}`}
                                  className="object-cover w-10 h-10 rounded-full"
                                />
                                <div className="ml-4">
                                  <div className="flex items-center text-sm font-medium text-neutral-900">
                                    {member.roleName === RoleEnum.DOCTOR
                                      ? "Dr. "
                                      : ""}
                                    {member.firstName} {member.lastName}
                                    {member.requiresPasswordChange && (
                                      <span className="inline-flex items-center px-2 py-0.5 ml-2 text-xs font-medium text-yellow-800 bg-yellow-100 border border-yellow-300 rounded-full">
                                        <RefreshCw className="mr-1 w-3 h-3" />
                                        {t(
                                          "staff.manage.list.passwordChangeRequired",
                                          "Password Required",
                                        )}
                                      </span>
                                    )}
                                  </div>
                                  <div className="text-sm text-neutral-500">
                                    {member.healthcareFacilities?.[0]?.name ||
                                      member.hospitalName ||
                                      ""}
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center">
                                <div className="flex items-center">
                                  {renderRoleInfo(member.roleName)}
                                </div>
                              </div>
                              {member.specialties?.[0] && (
                                <div className="text-sm text-neutral-500">
                                  {member.specialties[0].label}
                                </div>
                              )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-neutral-900">
                                {member.contactInfo?.email ||
                                  member.email ||
                                  t("staff.manage.list.notAvailable")}
                              </div>
                              <div className="text-sm text-neutral-500">
                                {member.contactInfo?.phoneNumber ||
                                  member.phoneNumber
                                  ? formatPhoneNumberForDisplay(
                                    member.contactInfo?.phoneNumber ||
                                    member.phoneNumber
                                  )
                                  : t("staff.manage.list.notAvailable")}
                              </div>
                            </td>
                            {filterRole === RoleEnum.DOCTOR && (
                              <>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <div className="flex items-center">
                                    <Star className="mr-1 w-4 h-4 text-yellow-500" />
                                    <span className="text-sm text-neutral-900">
                                      {member.averageRate?.toFixed(1) ||
                                        t("staff.manage.list.notAvailable")}
                                    </span>
                                  </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <div className="text-sm text-neutral-900">
                                    {member.nextAvailability
                                      ? t("staff.manage.list.available")
                                      : t("staff.manage.list.unavailable")}
                                  </div>
                                </td>
                              </>
                            )}
                            <td className="px-6 py-4 text-sm font-medium whitespace-nowrap">
                              <div className="flex space-x-2">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => onEditStaff(member)}
                                >
                                  <Edit className="w-4 h-4" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* Pagination */}
          {totalStaffCount > 0 && (
            <Pagination
              totalItems={totalStaffCount}
              itemsPerPage={itemsPerPage}
              currentPage={currentPage}
              onPageChange={handlePageChange}
              onItemsPerPageChange={handlePageSizeChange}
              isLoading={isLoading}
            />
          )}
        </div>
      )}
    </div>
  );
};
